import { z } from 'zod';
import { isIsoDate } from '../dates';
import { CheckpointKind, PolicyScope, WorkRole } from '../enums';
import { Enforcement, RULE_CATALOG, RuleKey, isRuleKey } from './rules';

/** Identificador estável de checkpoint dentro de um template (ex.: "qualificacao"). */
export const checkpointKeySchema = z
  .string()
  .min(2)
  .max(60)
  .regex(/^[a-z0-9][a-z0-9_-]*$/, 'Use letras minúsculas, números, "-" ou "_"');

export const isoDateSchema = z.string().refine(isIsoDate, 'Data inválida (use AAAA-MM-DD)');

export const dueRuleSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('absolute'), date: isoDateSchema }),
  z.object({
    type: z.literal('relative'),
    /** "work.start", "work.end" ou "checkpoint:<key>" */
    anchor: z.string().regex(/^(work\.start|work\.end|checkpoint:[a-z0-9][a-z0-9_-]*)$/, 'Âncora inválida'),
    offset: z.number().int().min(-3650).max(3650),
    unit: z.enum(['days', 'weeks', 'months']).default('days'),
  }),
]);
export type DueRule = z.infer<typeof dueRuleSchema>;

const workRoleEnum = z.enum(Object.values(WorkRole) as [WorkRole, ...WorkRole[]]);

/** Definição (possivelmente parcial) de checkpoint em uma camada de política. */
export const checkpointTemplateSchema = z.object({
  key: checkpointKeySchema,
  title: z.string().min(2).max(160).optional(),
  description: z.string().max(4000).optional(),
  kind: z.enum([CheckpointKind.CHECKPOINT, CheckpointKind.MILESTONE]).optional(),
  required: z.boolean().optional(),
  order: z.number().int().min(0).max(10_000).optional(),
  dependsOn: z.array(checkpointKeySchema).max(20).optional(),
  due: dueRuleSchema.optional(),
  evidenceRequired: z.array(z.string().min(1).max(300)).max(20).optional(),
  acceptanceCriteria: z.string().max(4000).optional(),
  responsibleRole: workRoleEnum.optional(),
  reviewerRole: workRoleEnum.optional(),
  reminderDaysBefore: z.array(z.number().int().min(0).max(365)).max(5).optional(),
  /** Remove um checkpoint opcional herdado. */
  removed: z.boolean().optional(),
});
export type CheckpointTemplate = z.infer<typeof checkpointTemplateSchema>;

/** Checkpoint resolvido (todos os campos obrigatórios presentes). */
export interface ResolvedCheckpoint {
  key: string;
  title: string;
  description: string;
  kind: CheckpointKind;
  required: boolean;
  order: number;
  dependsOn: string[];
  due: DueRule;
  evidenceRequired: string[];
  acceptanceCriteria: string;
  responsibleRole: WorkRole;
  reviewerRole: WorkRole;
  reminderDaysBefore: number[];
  /** Camada que definiu o checkpoint pela primeira vez. */
  sourceScope: PolicyScope | 'WORK';
}

export const ruleEntrySchema = z.object({
  value: z.unknown(),
  enforcement: z.enum([Enforcement.DEFAULT, Enforcement.LOCKED, Enforcement.FLOOR, Enforcement.CEILING]).default('DEFAULT'),
});
export type RuleEntry = { value: unknown; enforcement: Enforcement };

/** Conteúdo de uma versão de política (camada). */
export const policyContentSchema = z
  .object({
    rules: z.record(z.string(), ruleEntrySchema).default({}),
    checkpoints: z.array(checkpointTemplateSchema).max(200).default([]),
  })
  .superRefine((content, ctx) => {
    for (const [key, entry] of Object.entries(content.rules)) {
      if (!isRuleKey(key)) {
        ctx.addIssue({ code: 'custom', path: ['rules', key], message: `Regra desconhecida: ${key}` });
        continue;
      }
      const def = RULE_CATALOG[key as RuleKey];
      const parsed = def.schema.safeParse(entry.value);
      if (!parsed.success) {
        ctx.addIssue({
          code: 'custom',
          path: ['rules', key, 'value'],
          message: `Valor inválido para "${def.label}": ${parsed.error.issues[0]?.message ?? ''}`,
        });
      }
      if ((entry.enforcement === 'FLOOR' || entry.enforcement === 'CEILING') && !def.numeric) {
        ctx.addIssue({
          code: 'custom',
          path: ['rules', key, 'enforcement'],
          message: `"${def.label}" não é numérica; use DEFAULT ou LOCKED`,
        });
      }
    }
    const seen = new Set<string>();
    content.checkpoints.forEach((cp, i) => {
      if (seen.has(cp.key)) {
        ctx.addIssue({ code: 'custom', path: ['checkpoints', i, 'key'], message: `Chave duplicada: ${cp.key}` });
      }
      seen.add(cp.key);
    });
  });
export type PolicyContent = z.infer<typeof policyContentSchema>;

export const emptyPolicyContent = (): PolicyContent => ({ rules: {}, checkpoints: [] });
